import type { FlattenedPortfolioItem } from "@/types";

/**
 * Checks if a portfolio item is available for selection/transfer
 *
 * @param item - The portfolio item to check
 * @returns True if the item has status "AVAILABLE"
 */
export function isItemAvailable(item: FlattenedPortfolioItem): boolean {
  return item.status === "AVAILABLE";
}

export function isItemWaitingForSale(item: FlattenedPortfolioItem): boolean {
  return item.status === "WAITING_FOR_SALE";
}

/** Sum the MWh `count` across the AVAILABLE items in the set (others don't contribute). */
export function sumAvailableMwh(items: FlattenedPortfolioItem[]): number {
  return items.reduce(
    (total, item) => (isItemAvailable(item) ? total + item.count : total),
    0
  );
}

/**
 * Selection policy that accepts any item. Use when the caller has already
 * restricted the visible set (e.g. via the table's `items` prop) or for
 * admin flows that should not be gated by status.
 */
export const allItemsSelectable = (_item: FlattenedPortfolioItem): boolean =>
  true;

/**
 * Whether `selectedCount` takes only part of `item`: the one definition of a partial row, shared by
 * the selection (its `?portItemIds=` encoding and partial count) and the count cell. An item the
 * cache no longer holds is never partial.
 */
export const isPartialCount = (
  selectedCount: number,
  item: Pick<FlattenedPortfolioItem, "count"> | undefined
): boolean => item !== undefined && selectedCount < item.count;
